import { z } from 'zod';
import type { Font, Styles } from '../graphql/query/getRetailerSettings';
import {
  designSystemFontFallback,
  escapeFontFamily,
  toFallbackFontStack,
  toFontStack,
} from './fontStack';
import { isSafeFontSrc } from './fontSrc';

const fontFaceSchema = z.object({
  fontFamily: z
    .string()
    .max(100)
    .regex(/^[\w\s-]+$/),
  fontWeight: z.enum(['300', 'normal', 'bold']).nullish(),
  src: z.string().refine(isSafeFontSrc),
});

const fontEntrySchema = z.object({
  fontFamily: z.string().nullable(),
  fontWeight: z.string().nullable(),
  src: z.string().nullable(),
  // Defaulted so a theme a session stored before the field existed still
  // restores, and assigns its fonts by position as it did then.
  role: z.enum(['HEADING', 'BODY']).nullable().default(null),
});

const themeColorsSchema = z.object({
  ctaColor: z.string().nullable(),
  primaryColor: z.string().nullable(),
  secondaryColor: z.string().nullable(),
  textColor: z.string().nullable(),
});

export const stylesSchema = z.object({
  font: z.array(fontEntrySchema),
  theme: themeColorsSchema.nullable(),
  themeVarOverrides: z.array(z.array(z.string()).nullable()).nullable(),
  // Defaulted so a theme a session stored before the field existed still
  // restores, rather than failing the parse and losing the whole theme.
  fontFallback: z.string().nullable().default(null),
});

type ThemeColorKey = keyof z.infer<typeof themeColorsSchema>;

const isNonBlankString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

// Unset colours arrive as null or blank, and a blank one would override the
// design-system default with nothing.
const pickSetThemeColors = (
  theme: Styles['theme'],
): Partial<Record<ThemeColorKey, string>> =>
  Object.fromEntries(
    themeColorsSchema.keyof().options.flatMap((key) => {
      const value = theme?.[key];
      return isNonBlankString(value) ? [[key, value]] : [];
    }),
  );

/**
 * Maps theme schema keys to their corresponding CSS custom properties.
 * Note: ctaColor is handled separately via generateCtaColorCss() to derive hover/pressed states.
 */
export const pdxThemeMapping: Partial<
  Record<ThemeColorKey | 'fontFamily', readonly string[]>
> = {
  primaryColor: [
    '--n-color-component-action-plain-content-default',
    '--n-color-border-focus-ring',
  ],
  // secondaryColor is unmapped: the token it used to set is not read by any
  // component.
  textColor: ['--n-color-content-neutral-base'],
  fontFamily: ['--n-font-family-base'],
} as const;

/**
 * Generates CSS custom properties for CTA color with derived hover/pressed states
 * using CSS color-mix() to darken the base color progressively.
 */
const generateCtaColorCss = (ctaColor: string): string => {
  return [
    `--n-color-component-action-primary-fill-default: ${ctaColor};`,
    `--n-color-component-action-primary-fill-hover: color-mix(in srgb, ${ctaColor}, black 12%);`,
    `--n-color-component-action-primary-fill-pressed: color-mix(in srgb, ${ctaColor}, black 40%);`,
  ].join('\n  ');
};

const generateFontFaceCss = (fontConfig: Font): string => {
  if (!fontConfig || fontConfig.length === 0) {
    return '';
  }

  return fontConfig
    .map((font) => {
      const parsed = fontFaceSchema.safeParse(font);
      if (!parsed.success) {
        // eslint-disable-next-line no-console
        console.warn('Rejected unsafe font-face payload', parsed.error.issues);
        return null;
      }

      const { fontFamily, fontWeight, src } = parsed.data;

      const escapedFamily = escapeFontFamily(fontFamily);

      const weightLine = fontWeight ? `  font-weight: ${fontWeight};\n` : '';

      return (
        `@font-face {\n` +
        `  font-family: ${escapedFamily};\n` +
        weightLine +
        `  font-display: swap;\n` +
        `  src: ${src}\n` +
        `}`
      );
    })
    .filter((rule): rule is string => rule !== null)
    .join('\n\n');
};

type FontRole = NonNullable<Font[number]['role']>;

/**
 * The stacks for the base and heading font properties, null where the
 * design-system default should stand.
 *
 * Fonts served with a role each dress that role alone, and a role with no
 * uploaded font stays on the design-system default rather than borrowing the
 * other role's face. The design system's heading font defaults to the base
 * font, so a body-only upload pins headings to the plain design-system stack.
 *
 * Fonts served without a role keep the positional rule: the first for
 * headings and the second for body text, or a lone font for everything.
 */
const resolveFontStacks = (
  fonts: Font,
  merchantFallback: string | null,
): { base: string | null; heading: string | null } => {
  const loadableFamilies = new Set(
    fonts.flatMap((font) => {
      const parsed = fontFaceSchema.safeParse(font);
      return parsed.success ? [parsed.data.fontFamily] : [];
    }),
  );
  // A family with no @font-face written would only make the browser fall
  // through to the rest of the stack, so it keeps its slot but is left out.
  const stackOf = (family: string | null | undefined) => {
    if (!family) return null;
    return loadableFamilies.has(family)
      ? toFontStack(family, merchantFallback)
      : toFallbackFontStack(merchantFallback);
  };

  // The BFF serves either Command's fonts, every one with a role, or the
  // experience's, none with one, so one role marks the whole set as Command's.
  if (fonts.some((font) => font.role)) {
    const familyFor = (role: FontRole) =>
      fonts.find((font) => font.role === role && font.fontFamily)?.fontFamily;
    const base = stackOf(familyFor('BODY'));
    const heading =
      stackOf(familyFor('HEADING')) ??
      (base ? designSystemFontFallback.join(', ') : null);
    return { base, heading };
  }

  const families = fonts.flatMap((font) =>
    font.fontFamily ? [font.fontFamily] : [],
  );
  if (families.length >= 2) {
    return { base: stackOf(families[1]), heading: stackOf(families[0]) };
  }
  return { base: stackOf(families[0]), heading: null };
};

export const headerColorsSchema = z.object({
  backgroundColor: z.string().nullish(),
  textColor: z.string().nullish(),
});

export type HeaderColors = z.infer<typeof headerColorsSchema>;

/**
 * Replace the page favicon with a branded one from the retailer's MX assets.
 * Creates or updates the `<link rel="icon">` element in the document head.
 * Only allows https: URLs to prevent injection of unexpected protocols.
 */
export const injectFavicon = (href: string) => {
  try {
    const url = new URL(href);
    if (url.protocol !== 'https:') return;
  } catch {
    return;
  }

  document
    .querySelectorAll<HTMLLinkElement>(
      'link[rel="icon"], link[rel="shortcut icon"]',
    )
    .forEach((el) => el.remove());

  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = href;
  document.head.appendChild(link);
};

export const injectRetailerTheme = ({
  styles,
  headerColors,
  options,
}: {
  options?: {
    id?: string;
    layer?: string;
    retailer?: string | null;
  };
  styles: Styles;
  headerColors?: HeaderColors;
}) => {
  const { theme } = styles;
  const { id = 'retailerTheme', layer = 'retailer', retailer } = options || {};

  const themeStyleTag = document.getElementById(id);

  if (
    themeStyleTag &&
    themeStyleTag.getAttribute('data-retailer') === retailer
  ) {
    return;
  }

  const setThemeColors = pickSetThemeColors(theme);

  const fontFaceCss = generateFontFaceCss(styles?.font);

  const fontStacks = resolveFontStacks(styles?.font ?? [], styles.fontFallback);

  const hasSetThemeColors = Object.keys(setThemeColors).length > 0;
  const hasFontFaces = fontFaceCss.length > 0;

  let cssCustomProperties = '';
  if (hasSetThemeColors) {
    const standardProperties = Object.entries(setThemeColors)
      .filter(([key]) => key !== 'ctaColor')
      .map((entry) => {
        const [key, value] = entry as [ThemeColorKey, string];
        let pdxThemeMappedValue = pdxThemeMapping[key];

        // Temporary StitchFix override - remove when redundant
        if (
          pdxThemeMappedValue &&
          key === 'textColor' &&
          retailer === 'stitchfix' &&
          !pdxThemeMappedValue.includes(
            '--n-color-component-action-primary-content-default',
          )
        ) {
          pdxThemeMappedValue = [
            ...pdxThemeMappedValue,
            '--n-color-component-action-primary-content-default',
          ];
        }

        return pdxThemeMappedValue && pdxThemeMappedValue.length > 0
          ? pdxThemeMappedValue
              .map((cssCustomProp) => `${cssCustomProp}: ${value};`)
              .join('\n  ')
          : null;
      })
      .filter((prop): prop is string => prop !== null)
      .join('\n  ');

    // Generate CTA color with derived hover/pressed states using color-mix()
    const ctaColorCss = setThemeColors.ctaColor
      ? generateCtaColorCss(setThemeColors.ctaColor)
      : '';

    cssCustomProperties = [standardProperties, ctaColorCss]
      .filter(Boolean)
      .join('\n  ');
  }

  const fontFamilyCssCustomProperty =
    fontStacks.base && pdxThemeMapping.fontFamily?.[0]
      ? `${pdxThemeMapping.fontFamily[0]}: ${fontStacks.base};`
      : '';

  const headingFontCssCustomProperty = fontStacks.heading
    ? `--n-font-family-heading: ${fontStacks.heading};`
    : '';

  const headerBackgroundColor = headerColors?.backgroundColor?.trim();
  const headerTextColor = headerColors?.textColor?.trim();
  const headerColorCss = [
    headerBackgroundColor &&
      `--n-color-branded-header-bg: ${headerBackgroundColor};`,
    headerTextColor && `--n-color-branded-header-text: ${headerTextColor};`,
  ]
    .filter(Boolean)
    .join('\n  ');

  let rootBlockContent = [
    cssCustomProperties,
    fontFamilyCssCustomProperty,
    headingFontCssCustomProperty,
    headerColorCss,
  ]
    .filter(Boolean)
    .join('\n  ');

  // Add themeVarOverrides as CSS custom properties in :root
  let themeVarOverridesBlock = '';
  if (
    Array.isArray(styles.themeVarOverrides) &&
    styles.themeVarOverrides.length > 0
  ) {
    themeVarOverridesBlock = styles.themeVarOverrides
      .map((pair) => {
        if (
          Array.isArray(pair) &&
          pair.length === 2 &&
          typeof pair[0] === 'string' &&
          typeof pair[1] === 'string'
        ) {
          const [cssVar, value] = pair;
          return `${cssVar}: ${value};`;
        }
        return '';
      })
      .filter(Boolean)
      .join('\n  ');
  }

  // Combine all :root custom properties
  const allRootBlockContent = [rootBlockContent, themeVarOverridesBlock]
    .filter(Boolean)
    .join('\n  ');

  if (!hasFontFaces && !allRootBlockContent) return;

  const style = document.createElement('style');
  style.id = id;
  style.setAttribute('data-layer', layer);
  if (retailer) style.setAttribute('data-retailer', retailer);

  let cssContent = `@layer ${layer} {\n`;
  if (hasFontFaces) {
    cssContent += `\n${fontFaceCss}\n\n`;
  }
  if (allRootBlockContent) {
    cssContent += `  :root {\n  ${allRootBlockContent} \n  }\n`;
    cssContent += `  html { font-size: var(--n-font-size-base, 1rem); }\n`;
  }
  cssContent += `}`;

  style.textContent = cssContent;

  document.head.appendChild(style);
};
